import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ComboBox,
  ComposedModal,
  InlineLoading,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioTile,
  TextInput,
  TileGroup,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { debounce } from 'lodash-es';

import { api } from 'lib/request/api';
import type { FailedApiResponse } from 'components/well-page-content/types';
import type { TypeCurveWorkspaceResponse } from 'types/type-curve-workspace';
import type {
  TypeCurveScenariosResponse,
  TypeCurveHeaderQueryResponse,
} from 'types/api-responses';
import {
  createTypeCurveWorkspaceAtom,
  createTopLevelTcWorkspaceAtom,
} from './atoms';
import { usePortaledListBoxMenu } from './use-portaled-list-box-menu';

import style from './create-type-curve-workspace-modal.module.scss';

type WorkspaceSource = 'blank' | 'from_existing';

export interface CreateTypeCurveWorkspaceModalProps {
  open: boolean;
  /** Type curve header `resources.workspaces.href`. Discriminates the two modes:
   *  null means "listing page" mode with the full form, non-null means TC details
   *  page mode. Not posted to — that collection route is GET-only (routes.rb:
   *  `resources :workspaces, only: %i[index]`); both modes create through
   *  `POST /type_curve_workspaces`. */
  workspacesCollectionHref: string | null;
  /** TC details page mode: id of the `type_curve_header_details` row the page is
   *  showing. The workspace is cloned from it, exactly as picking this Type Curve
   *  under "From existing Type Curve" on the listing does. */
  sourceTypeCurveDetailId?: number | null;
  /** When opened from a TC details page, the scenario this type curve belongs to.
   *  The scenario ComboBox auto-selects the matching entry once options load. */
  defaultScenarioId?: number | null;
  onClose: () => void;
  /** Called on success in TC details page mode. */
  onCreated?: (workspace: TypeCurveWorkspaceResponse) => void;
  /** Called on success in listing page mode. */
  onCreatedTopLevel?: (workspace: TypeCurveWorkspaceResponse) => void;
}

const SEARCH_DEBOUNCE_MS = 400;
// First page size for both dropdowns. There is no minimum search length: the
// options load on open so the user sees a list before typing anything.
const DROPDOWN_PAGE_SIZE = 100;

// ─── Component ───────────────────────────────────────────────────────────────

export const CreateTypeCurveWorkspaceModal = ({
  open,
  workspacesCollectionHref,
  sourceTypeCurveDetailId,
  defaultScenarioId,
  onClose,
  onCreated,
  onCreatedTopLevel,
}: CreateTypeCurveWorkspaceModalProps) => {
  const isListingMode = workspacesCollectionHref === null;

  // Shared state
  const [name, setName] = useState('');
  const [nameBlurred, setNameBlurred] = useState(false);

  // Listing-mode only state
  const [source, setSource] = useState<WorkspaceSource>('blank');
  const [selectedScenario, setSelectedScenario] =
    useState<TypeCurveScenariosResponse | null>(null);
  const [selectedTypeCurve, setSelectedTypeCurve] =
    useState<TypeCurveHeaderQueryResponse | null>(null);
  const [scenarioTouched, setScenarioTouched] = useState(false);
  const [tcTouched, setTcTouched] = useState(false);
  const [scenarioSearch, setScenarioSearch] = useState('');
  const [tcSearch, setTcSearch] = useState('');

  // TC details page mode atom
  const [{ loading: legacyLoading, result: legacyResult }, createWorkspace] =
    useAtom(createTypeCurveWorkspaceAtom);

  // Listing page mode atom
  const [{ loading: topLevelLoading, result: topLevelResult }, createTopLevel] =
    useAtom(createTopLevelTcWorkspaceAtom);

  const loading = isListingMode ? topLevelLoading : legacyLoading;
  const result = isListingMode ? topLevelResult : legacyResult;

  // Scenario options. Same endpoint + view the listing's Scenarios tab uses, so
  // both surfaces resolve scenarios identically. Fires as soon as the modal
  // opens with an empty `text` for the first page, then re-queries the server on
  // each (debounced) keystroke — no client-side filtering.
  const { data: scenarioResults, isFetching: scenarioFetching } = useQuery({
    queryKey: ['ws-modal-scenario-search', scenarioSearch],
    queryFn: ({ signal }) =>
      api
        .post('queries/type_curve_headers', {
          json: {
            query: {
              view: 'scenarios',
              // Always sent, `""` included — an empty string means "first page,
              // unfiltered". Do not collapse to undefined: JSON.stringify drops
              // undefined keys, so `text` would disappear from the payload.
              text: scenarioSearch,
              pageSize: DROPDOWN_PAGE_SIZE,
            },
          },
          signal,
        })
        .json<{ results: TypeCurveScenariosResponse[]; totalCount: number }>(),
    enabled: open,
    staleTime: 30_000,
  });

  // Type Curve options — same contract, gated on the "from existing" source.
  const { data: tcResults, isFetching: tcFetching } = useQuery({
    queryKey: ['ws-modal-tc-search', tcSearch],
    queryFn: ({ signal }) =>
      api
        .post('queries/type_curve_headers', {
          json: {
            query: {
              view: 'type_curves',
              text: tcSearch,
              pageSize: DROPDOWN_PAGE_SIZE,
            },
          },
          signal,
        })
        .json<{
          results: TypeCurveHeaderQueryResponse[];
          totalCount: number;
        }>(),
    enabled: open && isListingMode && source === 'from_existing',
    staleTime: 30_000,
  });

  const debouncedSetScenarioSearch = useMemo(
    () =>
      debounce((text: string) => setScenarioSearch(text), SEARCH_DEBOUNCE_MS),
    []
  );

  const debouncedSetTcSearch = useMemo(
    () => debounce((text: string) => setTcSearch(text), SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    if (open) {
      setName('');
      setNameBlurred(false);
      setSource('blank');
      setSelectedScenario(null);
      setSelectedTypeCurve(null);
      setScenarioTouched(false);
      setTcTouched(false);
      setScenarioSearch('');
      setTcSearch('');
    }
  }, [open]);

  // Auto-select the type curve's own scenario when opening from TC details page
  useEffect(() => {
    if (
      !open ||
      selectedScenario !== null ||
      !defaultScenarioId ||
      !scenarioResults?.results?.length
    )
      return;

    const match = scenarioResults.results.find(
      (s) => s.id === defaultScenarioId
    );
    if (match) setSelectedScenario(match);
  }, [open, selectedScenario, defaultScenarioId, scenarioResults]);

  /* Hosts whichever dropdown menu is open at the end of <body>, so the modal
     footer can never paint over it. */
  const menuPortalContainerRef = usePortaledListBoxMenu<HTMLDivElement>(
    open,
    style.menuPortalHost
  );

  const nameTrimmed = name.trim();
  const nameInvalid = nameBlurred && nameTrimmed.length === 0;
  const scenarioInvalid = scenarioTouched && selectedScenario === null;
  const tcInvalid =
    tcTouched && source === 'from_existing' && selectedTypeCurve === null;

  // Heading with the fetch spinner beside it (Carbon's `titleText` takes a node),
  // so the loader sits next to the label rather than under the field.
  const headingWithLoader = (label: string, fetching: boolean) => (
    <span className={style.dropdownHeading}>
      {label}
      {fetching && (
        <InlineLoading
          status="active"
          iconDescription="Loading options"
          className={style.headingLoader}
        />
      )}
    </span>
  );

  const canSubmitListingMode =
    nameTrimmed.length > 0 &&
    selectedScenario !== null &&
    (source === 'blank' || selectedTypeCurve !== null);

  // Scenario is mandatory here too, same as on the listing.
  const canSubmitLegacyMode =
    nameTrimmed.length > 0 &&
    selectedScenario !== null &&
    Boolean(sourceTypeCurveDetailId);

  const canSubmit = isListingMode ? canSubmitListingMode : canSubmitLegacyMode;

  const errorPayload: FailedApiResponse | undefined =
    result?.type === 'error' ? (result.data as FailedApiResponse) : undefined;
  const createErrorSubtitle =
    errorPayload?.errors
      ?.map((e) => e.message)
      .filter((m) => m && m.trim().length > 0)
      .join(' ') || 'Unable to create workspace. Please try again.';
  const firstErr = errorPayload?.errors?.[0];
  const nameTakenMessage = firstErr?.code === 'NAME' ? firstErr.message : null;

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleSubmit = () => {
    if (!canSubmit || loading) return;

    if (isListingMode) {
      createTopLevel({
        type: 'POST',
        url: '/type_curve_workspaces',
        data: {
          workspace: {
            name: nameTrimmed,
            type_curve_scenario_id: selectedScenario!.id,
            ...(source === 'from_existing' &&
              selectedTypeCurve !== null && {
                originated_from_type_curve_header_details_id:
                  selectedTypeCurve.id,
              }),
          },
        },
        onSuccess: (data) => {
          window.open(data.href, '_blank');
          onCreatedTopLevel?.(data);
          onClose();
        },
      });
    } else {
      if (!sourceTypeCurveDetailId || selectedScenario === null) return;

      // Same endpoint and payload as the listing's "From existing Type Curve",
      // with this page's own header detail as the clone source. NOT the header's
      // `resources.workspaces.href` — that collection is GET-only, so posting to
      // it returned a routing error.
      createWorkspace({
        type: 'POST',
        url: '/type_curve_workspaces',
        data: {
          workspace: {
            name: nameTrimmed,
            type_curve_scenario_id: selectedScenario.id,
            originated_from_type_curve_header_details_id:
              sourceTypeCurveDetailId,
          },
        },
        onSuccess: (data) => {
          // Same as the listing's create: the new workspace opens in its own tab
          // and this page stays put. onCreated still runs so the page refreshes
          // its workspace list and auto-verifies unit overlaps.
          window.open(data.href, '_blank');
          onCreated?.(data);
          onClose();
        },
      });
    }
  };

  return (
    <ComposedModal
      open={open}
      onClose={handleClose}
      preventCloseOnClickOutside
      size="sm"
      className={style.modal}
    >
      <ModalHeader label="Type Curve" title="Create Workspace" />
      <ModalBody aria-label="Create Type Curve workspace">
        <div className={style.body} ref={menuPortalContainerRef}>
          <p className={style.intro}>
            Inside the Workspace you can mark this Type Curve correct or
            incorrect and promote your verification to Insights. Past Workspaces
            stay on this page as a record of the work that has been completed.
          </p>

          {/* Error notifications */}
          {!isListingMode && !sourceTypeCurveDetailId && (
            <InlineNotification
              kind="error"
              title="Missing Type Curve"
              subtitle="This Type Curve response did not include an id to create the workspace from. Refresh the page or contact support."
              lowContrast
              hideCloseButton
              className={style.notification}
            />
          )}
          {result?.type === 'error' && (
            <InlineNotification
              kind="error"
              title="Error!"
              subtitle={createErrorSubtitle}
              lowContrast
              hideCloseButton
              className={style.notification}
            />
          )}
          {result?.type === 'fatal' && (
            <InlineNotification
              kind="error"
              title="Error!"
              subtitle={result.message}
              lowContrast
              hideCloseButton
              className={style.notification}
            />
          )}

          {/* 1. Name — always first */}
          <TextInput
            id="create-tc-workspace-name"
            labelText="Workspace name"
            placeholder="Enter workspace name"
            value={name}
            disabled={loading}
            autoFocus
            invalid={
              nameInvalid ||
              result?.type === 'error' ||
              result?.type === 'fatal'
            }
            invalidText={
              nameInvalid
                ? 'Enter a workspace name.'
                : result?.type === 'fatal'
                  ? result.message
                  : (nameTakenMessage ?? undefined)
            }
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setNameBlurred(true)}
          />

          {/* 2. Radio tiles — listing mode only */}
          {isListingMode && (
            <TileGroup
              legend="Workspace source"
              name="tc-workspace-source"
              valueSelected={source}
              onChange={(val: string) =>
                setSource((val as WorkspaceSource) || 'blank')
              }
              className={style.sourceGroup}
            >
              <RadioTile value="blank" id="tc-ws-source-blank">
                <span className={style.sourceOption}>
                  <span className={style.sourceLabel}>Blank workspace</span>
                  <span className={style.sourceDesc}>
                    Creates a new Type Curve. Start from scratch, build its
                    inventory in the workspace, and promote it to Insights when
                    it&apos;s verified.
                  </span>
                </span>
              </RadioTile>
              <RadioTile value="from_existing" id="tc-ws-source-existing">
                <span className={style.sourceOption}>
                  <span className={style.sourceLabel}>
                    From existing Type Curve
                  </span>
                  <span className={style.sourceDesc}>
                    Opens a workspace on a Type Curve that already exists.
                    Review or refine its current inventory, then promote your
                    verification to Insights.
                  </span>
                </span>
              </RadioTile>
            </TileGroup>
          )}

          {/* 3. Scenario — shown in both modes */}
          <ComboBox<TypeCurveScenariosResponse>
            id="tc-ws-scenario"
            titleText={headingWithLoader(
              'Type Curve Scenario',
              scenarioFetching
            )}
            placeholder="Search scenarios..."
            items={scenarioResults?.results ?? []}
            itemToString={(item) => item?.name ?? ''}
            selectedItem={selectedScenario}
            onChange={({ selectedItem }) => {
              setScenarioTouched(true);
              setSelectedScenario(selectedItem ?? null);
            }}
            onInputChange={(text) =>
              debouncedSetScenarioSearch((text ?? '').trim())
            }
            onBlur={() => setScenarioTouched(true)}
            shouldFilterItem={() => true}
            invalid={scenarioInvalid}
            invalidText={scenarioInvalid ? 'Select a Type Curve Scenario.' : ''}
            disabled={loading}
          />

          {/* 4. TC — from_existing only, listing mode */}
          {isListingMode && source === 'from_existing' && (
            <ComboBox<TypeCurveHeaderQueryResponse>
              id="tc-ws-tc"
              titleText={headingWithLoader('Type Curve', tcFetching)}
              placeholder="Search type curves..."
              items={tcResults?.results ?? []}
              itemToString={(item) => item?.uniqueTcId ?? ''}
              selectedItem={selectedTypeCurve}
              onChange={({ selectedItem }) => {
                setTcTouched(true);
                setSelectedTypeCurve(selectedItem ?? null);
              }}
              onInputChange={(text) =>
                debouncedSetTcSearch((text ?? '').trim())
              }
              onBlur={() => setTcTouched(true)}
              shouldFilterItem={() => true}
              invalid={tcInvalid}
              invalidText={tcInvalid ? 'Select a Type Curve.' : ''}
              disabled={loading}
            />
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          kind="secondary"
          className={style.cancelButton}
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          kind="primary"
          onClick={handleSubmit}
          disabled={!canSubmit || loading}
        >
          {loading ? 'Creating…' : 'Create Workspace'}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};
